import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal } from "./Reveal";

const IMAGES = {
  A: "https://images.pexels.com/photos/32167663/pexels-photo-32167663.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940",
  B: "https://images.unsplash.com/photo-1747191092806-c7da11881986?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxyZXNpbiUyMHN0YXR1ZSUyMHNjdWxwdHVyZSUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NDg1MTI4NHww&ixlib=rb-4.1.0&q=85",
  C: "https://images.unsplash.com/photo-1611650034971-11897caaf839?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxyZXNpbiUyMHN0YXR1ZSUyMHNjdWxwdHVyZSUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NDg1MTI4NHww&ixlib=rb-4.1.0&q=85",
  D: "https://images.unsplash.com/photo-1421076644408-71a59229cc46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxtaW5pYXR1cmUlMjBtb2RlbCUyMG1hY3JvJTIwcGhvdG9ncmFwaHl8ZW58MHx8fHwxNzg0ODUxMjgzfDA&ixlib=rb-4.1.0&q=85",
};

const PIECES = [
  {
    img: IMAGES.A,
    ref: "Categoria",
    title: "Presentes",
    edition: "Aniversários · Datas",
    aspect: "aspect-[3/4]",
  },
  {
    img: IMAGES.B,
    ref: "Categoria",
    title: "Homenagens",
    edition: "Memórias · Tributo",
    aspect: "aspect-[4/5]",
  },
  {
    img: IMAGES.C,
    ref: "Categoria",
    title: "Decoração",
    edition: "Casa · Ambiente",
    aspect: "aspect-square",
  },
  {
    img: IMAGES.D,
    ref: "Categoria",
    title: "Lembranças",
    edition: "Momentos únicos",
    aspect: "aspect-[16/9]",
  },
];

export default function ChapterColecao() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section
      id="acervo"
      ref={ref}
      className="relative py-32 md:py-48 overflow-hidden"
      data-testid="chapter-acervo"
    >
      <motion.div
        style={{ y: bgY }}
        className="pointer-events-none absolute -top-10 -right-10 font-serif-display text-[26vw] leading-none text-[var(--ink)] opacity-[0.035] select-none italic"
      >
        Acervo
      </motion.div>

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex items-center gap-6 mb-14 md:mb-20">
            <span className="font-mono-tech text-[11px] text-[var(--accent)]">
              Capítulo 03
            </span>
            <span className="w-24 h-px bg-[var(--line-2)]" />
            <span className="font-mono-tech text-[11px] text-[var(--ink-2)]">
              O acervo
            </span>
          </div>
        </Reveal>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24">
          <Reveal>
            <h2 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-[-0.03em] max-w-[16ch]">
              Um acervo criado com{" "}
              <span className="italic text-[var(--ink-2)]">carinho e propósito.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-sans text-[15px] leading-[1.75] text-[var(--ink-2)] max-w-[40ch]">
              Cada peça é feita sob encomenda, do jeitinho que você imaginou.
              Do presente inesquecível à decoração afetiva — passando por
              homenagens, lembranças e detalhes que ganham vida em 3D.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-10">
          <Reveal className="col-span-12 md:col-span-7 md:row-span-2">
            <PieceCard piece={PIECES[0]} tall />
          </Reveal>
          <Reveal delay={0.05} className="col-span-6 md:col-span-5">
            <PieceCard piece={PIECES[1]} />
          </Reveal>
          <Reveal delay={0.1} className="col-span-6 md:col-span-5">
            <PieceCard piece={PIECES[2]} />
          </Reveal>
          <Reveal delay={0.1} className="col-span-12">
            <PieceCard piece={PIECES[3]} wide />
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-16 md:mt-24 flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-t border-[var(--line)] pt-8">
            <div className="font-mono-tech text-[11px] text-[var(--ink-2)]">
              Cada peça &nbsp;·&nbsp; uma história &nbsp;·&nbsp; feita pra você
            </div>
            <a
              href="https://www.instagram.com/acervo_3d/"
              target="_blank"
              rel="noreferrer noopener"
              className="group inline-flex items-center gap-4 font-mono-tech text-[11px] text-[var(--ink)] link-under"
              data-testid="cta-instagram-collection"
            >
              Ver mais no Instagram · @acervo_3d
              <span className="inline-block w-8 h-px bg-current group-hover:w-14 transition-[width] duration-500" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PieceCard({ piece, tall, wide }) {
  return (
    <figure className="group">
      <div
        className={`spotlight relative overflow-hidden ${
          tall ? "aspect-[3/4] md:aspect-[3/4.4]" : wide ? "aspect-[16/9]" : piece.aspect
        }`}
      >
        <img
          src={piece.img}
          alt={piece.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-4 left-4 z-10 font-mono-tech text-[10px] text-[var(--ink)]">
          {piece.ref}
        </div>
        <div className="absolute bottom-4 right-4 z-10 font-mono-tech text-[10px] text-[var(--ink)]">
          {piece.edition}
        </div>
      </div>
      <figcaption className="mt-4 flex items-baseline justify-between">
        <h3 className="font-serif-display text-3xl md:text-4xl tracking-tight leading-none italic">
          {piece.title}
        </h3>
        <span className="font-mono-tech text-[10px] text-[var(--ink-3)]">
          {piece.edition}
        </span>
      </figcaption>
    </figure>
  );
}
